import { CaretRightIcon, CheckIcon, CircleIcon } from "@phosphor-icons/react";
import {
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  type MenuItemProps as AriaMenuItemProps,
  type MenuProps as AriaMenuProps,
  MenuTrigger as AriaMenuTrigger,
  Separator as AriaSeparator,
  type SeparatorProps as AriaSeparatorProps,
  SubmenuTrigger as AriaSubmenuTrigger,
  composeRenderProps,
  type PopoverProps,
} from "react-aria-components";

import { cn } from "@/lib/utils";

import { SelectPopover } from "./select";

const MenuTrigger = AriaMenuTrigger;

const MenuSubTrigger = AriaSubmenuTrigger;

function MenuPopover({ className, ...props }: PopoverProps) {
  return (
    <SelectPopover
      className={composeRenderProps(className, (className) =>
        cn("w-auto", className),
      )}
      {...props}
    />
  );
}

const Menu = <T extends object>({ className, ...props }: AriaMenuProps<T>) => (
  <AriaMenu
    className={cn(
      "max-h-[inherit] overflow-auto rounded-md p-1 outline-solid outline-0 [clip-path:inset(0_0_0_0_round_calc(var(--radius)-2px))] bg-white",
      className,
    )}
    {...props}
  />
);

const MenuItem = ({ children, className, ...props }: AriaMenuItemProps) => (
  <AriaMenuItem
    textValue={
      props.textValue || (typeof children === "string" ? children : undefined)
    }
    className={composeRenderProps(className, (className) =>
      cn(
        "relative flex cursor-pointer select-none items-center gap-1.5 rounded-xs pl-1.5 pr-8 py-1.5 text-sm outline-hidden transition-colors",
        /* Disabled */
        "data-disabled:pointer-events-none data-disabled:opacity-50",
        /* Focused */
        "data-focused:bg-slate-100 data-focused:text-gray-900  dark:data-focused:bg-gray-800 dark:data-[focused]:text-grat-50",
        /* Selection Mode */
        "data-selection-mode:pl-8",
        className,
      ),
    )}
    {...props}
  >
    {composeRenderProps(children, (children, renderProps) => (
      <>
        <span className="absolute left-2 flex size-4 items-center justify-center">
          {renderProps.isSelected && (
            <>
              {renderProps.selectionMode === "single" && (
                <CircleIcon className="size-2 fill-current" />
              )}
              {renderProps.selectionMode === "multiple" && (
                <CheckIcon className="size-4" />
              )}
            </>
          )}
        </span>

        {children}

        {renderProps.hasSubmenu && (
          <CaretRightIcon className="ml-auto size-4" />
        )}
      </>
    ))}
  </AriaMenuItem>
);

const MenuSeparator = ({ className, ...props }: AriaSeparatorProps) => (
  <AriaSeparator
    className={cn("-mx-1 my-1 h-px bg-slate-200 dark:bg-slate-700", className)}
    {...props}
  />
);

export {
  Menu,
  MenuItem,
  MenuPopover,
  MenuSeparator,
  MenuSubTrigger,
  MenuTrigger,
};
